import camelcase from "camelcase-keys";
import { clsx } from "clsx";
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  ButtonToolbar,
  CloseButton,
  Col,
  Form,
  InputGroup,
  Row
} from "react-bootstrap";

import MultipleFileSelector from "../../forms/components/MultipleFileSelector";
import usePrepareReanalyzeData, {
  areAdditionalAnalysisTypesAvailable
} from "../../modules/header/hooks/usePrepareReanalyzeData";
import { UploadState } from "../../modules/header/upload.reducer";
import {
  InteractiveParams,
  RlcsErrors,
  RlcsParams,
  SourcesState
} from "../../modules/samples/components/reanalyze/reanalyze-form";
import ReanalyzeForm from "../../modules/samples/components/reanalyze/reanalyze-form/ReanalyzeForm";
import useCheckIntegrations from "../../modules/samples/hooks/useCheckIntegrations";
import {
  INITIAL_INTERACTIVE_PARAMS,
  INITIAL_RLCS_PARAMS
} from "../../modules/samples/components/reanalyze/reanalyze-popup/consts";
import { getLocaleForPlatform } from "../../modules/samples/components/reanalyze/reanalyze-popup/utils";
import { AnalysisSelections, HandleUploadParams, LayoutType } from "../@types";
import TooltipIcon from "../TooltipIcon";
import { INITIAL_FILE_UPLOAD_ANALYSIS_SELECTIONS, LAYOUT_TYPE } from "./consts";
import useUploadFromUrl from "./useUploadFromUrl";
import {
  filterNonLocalAnalyses,
  getDynamicAnalysisList,
  getObjectValue
} from "./utils";
import ToggleLink from "../toggle-link/ToggleLink";

type FileAnalysisFormProps = {
  closeModal: () => void;
  handleUpload: (args: HandleUploadParams) => void;
  onLayout?: (layout: LayoutType) => void;
  state: UploadState;
};

type FileAnalysisFormState = {
  files: Array<File>;
  fileUrl: string;
};

const INITIAL_VALUES: FileAnalysisFormState = {
  files: [],
  fileUrl: ""
};

const INITIAL_SOURCES: SourcesState = {
  sourceTag: "",
  fileOriginLink: ""
};

const convertToRLCSError = (error: UploadState["error"]): RlcsErrors => {
  const singleHashError = error?.[Object.keys(error || {})[0]];
  return {
    execution_timeout: singleHashError?.timeout?.[0],
    sample_name: singleHashError?.sample_name?.[0],
    internet_simulation: singleHashError?.internet_simulation?.[0]
  };
};

const convertUrlErrorToRLCSError = (
  error: Record<string, string[]>
): RlcsErrors => ({
  execution_timeout: error?.timeout?.[0],
  sample_name: error?.sample_name?.[0],
  internet_simulation: error?.internet_simulation?.[0]
});

const FileAnalysisForm: React.FC<FileAnalysisFormProps> = ({
  closeModal,
  handleUpload,
  onLayout,
  state
}) => {
  const filePickerRef = useRef<HTMLInputElement>(null);
  const previousSelections = useRef(INITIAL_FILE_UPLOAD_ANALYSIS_SELECTIONS);

  const [filePassword, setFilePassword] = useState();
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [values, setValues] = useState<FileAnalysisFormState>(INITIAL_VALUES);
  const [analyses, setAnalyses] = useState(
    INITIAL_FILE_UPLOAD_ANALYSIS_SELECTIONS
  );
  const [rlcsParams, setRlcsParams] = useState<RlcsParams>(INITIAL_RLCS_PARAMS);
  const [interactiveParams, setInteractiveParams] = useState<InteractiveParams>(
    INITIAL_INTERACTIVE_PARAMS
  );
  const [sources, setSources] = useState<SourcesState>(INITIAL_SOURCES);

  const { data: formData } = usePrepareReanalyzeData();
  const { tiCloud } = camelcase(formData ?? {});

  useEffect(() => {
    if (!tiCloud?.autoupload || !tiCloud?.enabled) return;
    setAnalyses(prevState => ({ ...prevState, ti_cloud: true }));
  }, [tiCloud?.autoupload, tiCloud?.enabled]);

  const selectFile = (event: React.UIEvent) => {
    event.preventDefault();
    filePickerRef.current?.click();
  };

  const {
    mutate: uploadFromUrl,
    isPending: isLoading,
    error: urlUploadError,
    reset: resetUrlUploadError
  } = useUploadFromUrl();
  const { checkIntegrations, isLoading: isCheckIntegrationsLoading } =
    useCheckIntegrations();

  const disableLocalFiles = Boolean(values.fileUrl);
  const disableUrl = Boolean(values.files?.length);

  const fileUploadIsEmpty = !(disableLocalFiles || disableUrl);

  const submitFileOrUrl = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const { localOnly, tiCloud: cloud } = camelcase(analyses);

    const effectiveAnalyses: AnalysisSelections = localOnly
      ? filterNonLocalAnalyses(analyses)
      : analyses;

    const dynamicAnalyses = getDynamicAnalysisList(effectiveAnalyses);
    const checkedIntegrations = await checkIntegrations(dynamicAnalyses);

    const availableIntegrations = dynamicAnalyses.filter(
      integration =>
        !(integration in checkedIntegrations) ||
        checkedIntegrations[integration]
    );

    const filteredAnalyses = Object.entries(effectiveAnalyses).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: key in checkedIntegrations ? checkedIntegrations[key] : value
      }),
      {} as AnalysisSelections
    );

    if (values.files.length) {
      const localeForPlatform = getLocaleForPlatform(
        rlcsParams.locale,
        rlcsParams.platform
      );

      const isRlcs = effectiveAnalyses.rl_dynamic_analysis;
      const isInteractive = effectiveAnalyses.rl_dynamic_analysis_interactive;

      const payload: HandleUploadParams = {
        files: values.files,
        password: filePassword,
        uploadToTicloud: cloud,
        rlcsParams: {
          platform: getObjectValue(rlcsParams, "platform", isRlcs),
          geolocation: getObjectValue(rlcsParams, "geolocation", isRlcs),
          locale: isRlcs ? localeForPlatform : undefined,
          sample_name: getObjectValue(rlcsParams, "sample_name", isRlcs),
          internet_simulation: getObjectValue(
            rlcsParams,
            "internet_simulation",
            isRlcs
          ),
          timeout: getObjectValue(rlcsParams, "execution_timeout", isRlcs)
        },
        rlcsInteractive: {
          platform: getObjectValue(
            interactiveParams,
            "platform",
            isInteractive
          ),
          sample_name: getObjectValue(
            interactiveParams,
            "sample_name",
            isInteractive
          ),
          timeout: getObjectValue(
            interactiveParams,
            "execution_timeout",
            isInteractive
          ),
          geolocation: getObjectValue(
            interactiveParams,
            "geolocation",
            isInteractive
          ),
          locale: getObjectValue(interactiveParams, "locale", isInteractive)
        },
        dynamicAnalysis: availableIntegrations,
        localOnly: analyses.local_only,
        sources: {
          sourceTag: sources.sourceTag,
          fileOriginLink: sources.fileOriginLink
        }
      };
      handleUpload(payload);
    } else {
      uploadFromUrl({
        analyses: filteredAnalyses,
        fileForm: values,
        rlcsParams,
        interactiveParams,
        sources
      }).then(() => {
        closeModal();
      });
    }
  };

  const openAdditionalConfigurationStep = () => {
    if (fileUploadIsEmpty) {
      return;
    }

    previousSelections.current = analyses;
    setIsConfigOpen(true);

    onLayout?.(
      areAdditionalAnalysisTypesAvailable(formData)
        ? LAYOUT_TYPE.LG
        : LAYOUT_TYPE.MD
    );
  };

  const closeAdditionalConfigurationStep = () => {
    onLayout?.(LAYOUT_TYPE.XL);
    setIsConfigOpen(false);
    setAnalyses(previousSelections.current);
  };

  const handleRLCSParamsChange = (newValues: RlcsParams) => {
    setRlcsParams(prev => ({
      ...prev,
      ...newValues
    }));
    resetUrlUploadError();
  };

  const handleInteractiveParamChange = (v: InteractiveParams) => {
    setInteractiveParams(prev => ({
      ...prev,
      ...v
    }));
    resetUrlUploadError();
  };

  const fileUploadStep = (
    <Form onSubmit={submitFileOrUrl}>
      <Row>
        <Col xs={5}>
          <Form.Group
            as={Row}
            className="mb-25"
            controlId="browse-local-files-input"
          >
            <Form.Label
              className={clsx(
                disableLocalFiles && "file-analysis-label-disabled",
                "file-analysis-label-1",
                "mt-15"
              )}
            >
              Browse Local Files
            </Form.Label>

            <Col xs={9}>
              <MultipleFileSelector
                filePickerRef={filePickerRef}
                files={Array.from(values.files ?? [])}
                setFiles={newFiles => setValues({ ...values, files: newFiles })}
                placeholder="Local File"
                isDisabled={disableLocalFiles}
                required
              />
            </Col>

            <Col xs={3}>
              <Button
                variant="prompt-secondary"
                onClick={selectFile}
                disabled={disableLocalFiles}
              >
                Browse Files
              </Button>
            </Col>
          </Form.Group>
        </Col>

        <Col className="file-analysis-border" xs={2}>
          <div className="file-analysis-border-text">OR</div>
        </Col>

        <Col xs={5}>
          <Form.Group
            as={Row}
            className="mb-25"
            controlId="enter-file-url-text-box"
          >
            <Form.Label
              className={clsx(
                disableUrl && "file-analysis-label-disabled",
                "file-analysis-label-1",
                "mt-15"
              )}
            >
              <div className="flex">
                <span>Enter a File URL</span>

                <TooltipIcon
                  icon="info-circle"
                  message="Provide a link to the file you want to analyze, e.g. https://www.example.org/file.pdf. Both HTTP and HTTPS protocols are supported."
                  className="file-analysis-tooltip-icon"
                />
              </div>
            </Form.Label>

            <InputGroup>
              <Form.Control
                type="text"
                className="file-analysis-text-input"
                placeholder="Enter URL"
                spellCheck="false"
                disabled={disableUrl}
                onChange={e => {
                  setValues({
                    ...values,
                    fileUrl: e.target.value
                  });
                }}
                value={values.fileUrl}
                required
              />

              <CloseButton
                className="file-analysis-text-clear"
                disabled={disableUrl}
                onClick={() => setValues({ ...values, fileUrl: "" })}
              />
            </InputGroup>
          </Form.Group>
        </Col>
      </Row>

      <div className="mt-20">
        <ToggleLink
          linkText="Advanced Analysis Options"
          handleClick={openAdditionalConfigurationStep}
          isDisabled={fileUploadIsEmpty}
        />
      </div>

      <hr className={"my-25"} />

      <ButtonToolbar className="file-analysis-buttons-right mt-0">
        <Button type="reset" variant="outline-primary" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={isLoading || isCheckIntegrationsLoading}
        >
          Submit
        </Button>
      </ButtonToolbar>
    </Form>
  );

  const additionalConfigurationStep = (
    <ReanalyzeForm
      initial={analyses}
      values={analyses}
      displayWarningMessage={values.files?.length > 5}
      popupWindowData={formData}
      onCancel={closeAdditionalConfigurationStep}
      onValues={(updatedValues: AnalysisSelections) => {
        if (
          updatedValues.rl_dynamic_analysis ||
          updatedValues.rl_dynamic_analysis_interactive
        ) {
          setAnalyses({ ...updatedValues, ti_cloud: true });
        } else {
          setAnalyses(updatedValues);
        }
      }}
      onSubmit={submitFileOrUrl}
      isSubmitted={isCheckIntegrationsLoading}
      onRlcsParamsChange={handleRLCSParamsChange}
      rlcsParams={rlcsParams}
      rlcsErrors={{
        ...convertToRLCSError(state.error),
        ...convertUrlErrorToRLCSError(urlUploadError?.data || {})
      }}
      interactiveParams={interactiveParams}
      onInteractiveParamChange={handleInteractiveParamChange}
      tcAnalysisDisabled
      submitButtonLabel="Submit"
      onFilePasswordChange={setFilePassword}
      ticloudAnalysisDisabled={
        analyses.rl_dynamic_analysis || analyses.rl_dynamic_analysis_interactive
      }
      interactiveAnalysisDisabled={values.files?.length > 1}
      showLocalOnlyAnalysis
      sources={sources}
      onSourcesChange={setSources}
      fileUrl={values.fileUrl}
    />
  );

  return isConfigOpen ? additionalConfigurationStep : fileUploadStep;
};

export default FileAnalysisForm;
